import { rem } from 'polished'
import React, { useState } from 'react'
import ReactPhoneInput from 'react-phone-input-2'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import styled, { css } from 'styled-components'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Hint } from 'components/Input'
import { Box, Flex } from 'components/Layout'
import { Tooltip } from 'components/Tooltip'
import { Text } from 'components/Typography'
import 'react-phone-input-2/lib/style.css'
import { useValidationErrorTrackingForGA4Provider } from 'providers/googleAnalytics/utils/ErrorTracking/useValidationErrorTrackingForGA4'
import {
  getBorderColor,
  HINT_HEIGHT,
  RADIUS,
  SPACE,
  INPUT_SIZE,
  COLOR_INTENT,
  mq,
  Z_INDEX,
} from 'Theme'

const TOOLTIP_OFFSET = 10
const TOOLTIP_ARROW_OFFSET = rem(19)
const HINT_MARGIN_TOP = SPACE.PX_5

const IconWrapper = styled(Box)`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  line-height: ${ICON_SIZE.PX_18};
  cursor: pointer;
  z-index: ${Z_INDEX.PHONE_INPUT_INFO_ICON};
  ${({ hasHint }) =>
    hasHint &&
    css`
      transform: translateY(
        calc(-50% - (${HINT_MARGIN_TOP} + ${HINT_HEIGHT}) / 2)
      );
    `}
  ${mq.to.TABLET`
    padding: ${SPACE.PX_15};
  `}
  padding-right: ${SPACE.PX_15};
`

const StyledTooltip: typeof Tooltip = styled(Tooltip)`
  & + div .rc-tooltip-arrow {
    right: ${TOOLTIP_ARROW_OFFSET};
  }
`

const getInputStyles = ({ isDisabled }) =>
  isDisabled
    ? {
        backgroundColor: COLOR_INTENT.INPUT.DISABLED_BACKGROUND,
        color: COLOR_INTENT.INPUT.DISABLED_TEXT,
      }
    : {
        backgroundColor: COLOR_INTENT.INPUT.BACKGROUND,
        color: COLOR_INTENT.INPUT.TEXT,
      }

interface PhoneInputProps {
  label: string
  onChange: (value: string) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  value: string | null
  name: string
  hint?: string
  hasHint?: boolean
  tooltip?: string
  isError?: boolean
  isDisabled?: boolean
}

export const PhoneInput: React.FC<React.PWC<PhoneInputProps>> = ({
  label,
  onChange,
  onBlur: onBlurHandler,
  value,
  name,
  hint,
  hasHint,
  tooltip,
  isError = false,
  isDisabled = false,
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const borderColor = getBorderColor({
    isFocusedOrChecked: isFocused,
    isError,
    allowedBorderColor: !isDisabled,
  })
  useValidationErrorTrackingForGA4Provider({
    hint,
    isError,
  })
  return (
    <Flex flexDirection="column">
      {label && (
        <Text display="block" mb={SPACE.PX_10}>
          {label}
        </Text>
      )}
      <Flex
        position="relative"
        display="inline-flex"
        flexDirection="column"
        height={
          hasHint
            ? `calc(${INPUT_SIZE.M} + ${HINT_MARGIN_TOP} + ${HINT_HEIGHT})`
            : 'initial'
        }
      >
        {tooltip && (
          <StyledTooltip
            body={tooltip}
            placement="topRight"
            align={{
              offset: [0, TOOLTIP_OFFSET],
            }}
          >
            <IconWrapper hasHint={hasHint}>
              <Icon.Info size={ICON_SIZE.PX_18} color={COLOR_INTENT.GRAY_50} />
            </IconWrapper>
          </StyledTooltip>
        )}
        <ReactPhoneInput
          country="us"
          value={value}
          onChange={(newValue) => {
            onChange(`+${newValue}`)
          }}
          preferredCountries={['us']}
          disabled={isDisabled}
          disableDropdown={isDisabled}
          inputProps={{ name, 'aria-label': 'phoneNumber' }}
          onFocus={() => {
            setIsFocused(true)
          }}
          onBlur={(event) => {
            setIsFocused(false)
            if (onBlurHandler) {
              onBlurHandler(event)
            }
          }}
          inputStyle={{
            width: '100%',
            borderColor,
            borderRadius: RADIUS.PX_6,
            height: INPUT_SIZE.M,
            ...getInputStyles({ isDisabled }),
          }}
          buttonStyle={{
            borderColor,
            ...(isDisabled
              ? { backgroundColor: COLOR_INTENT.INPUT.DISABLED_BACKGROUND }
              : {}),
          }}
        />
        {hint && (
          <Hint
            isError={isError}
            data-test-class={`${DATA_TEST_CLASS.PHONE_INPUT_HINT}-${name}`}
          >
            {hint}
          </Hint>
        )}
      </Flex>
    </Flex>
  )
}
